import * as z from "zod";

export enum OmsInsightType {
  // Non-actionable
  ON_TIME_FULFILLMENT_STREAK = "on_time_fulfillment_streak",
  ORDER_TO_SHIP_TIME_IMPROVEMENT = "order_to_ship_time_improvement",
  ORDER_TO_SHIP_TIME_DEGRADATION = "order_to_ship_time_degradation",
  COST_PER_OUNCE_IMPROVEMENT = "cost_per_ounce_improvement",
  COST_PER_OUNCE_DEGRADATION = "cost_per_ounce_degradation",
  TOTAL_SHIPPING_SPEND_MILESTONE = "total_shipping_spend_milestone",
  CHANNEL_CONCENTRATION = "channel_concentration",
  FULFILLMENT_VELOCITY_TREND = "fulfillment_velocity_trend",
  ADDRESS_CORRECTION_IMPACT = "address_correction_impact",
  PACKING_ERROR_REDUCTION = "packing_error_reduction",
  PACKING_ERROR_INCREASE = "packing_error_increase",
  THROUGHPUT_MILESTONE = "throughput_milestone",
  PICK_TO_PACK_IMPROVEMENT = "pick_to_pack_improvement",
  PICK_TO_PACK_DEGRADATION = "pick_to_pack_degradation",
  RECLAIM_REVENUE_RECOVERED = "reclaim_revenue_recovered",

  // Always-show summary insights (6 exist, 3 shown per period)
  PERIOD_SHIPMENT_SUMMARY = "period_shipment_summary",
  CARRIER_COST_SPREAD = "carrier_cost_spread",
  FASTEST_CARRIER = "fastest_carrier",
  BUSIEST_SHIPPING_DAY = "busiest_shipping_day",
  TOP_CARRIER_VOLUME = "top_carrier_volume",
  AVG_COST_PER_LABEL = "avg_cost_per_label",
}

// ---------------------------------------------------------------------------
// Per-type data shapes
// ---------------------------------------------------------------------------

const onTimeFulfillmentStreakInsight_ = z.object({
  type: z.literal(OmsInsightType.ON_TIME_FULFILLMENT_STREAK),
  data: z.object({ streakDays: z.number(), onTimeRate: z.number() }),
});

const orderToShipTimeImprovementInsight_ = z.object({
  type: z.literal(OmsInsightType.ORDER_TO_SHIP_TIME_IMPROVEMENT),
  data: z.object({
    currentAvgDays: z.number(),
    previousAvgDays: z.number(),
    improvementPct: z.number(),
  }),
});

const orderToShipTimeDegradationInsight_ = z.object({
  type: z.literal(OmsInsightType.ORDER_TO_SHIP_TIME_DEGRADATION),
  data: z.object({
    currentAvgDays: z.number(),
    previousAvgDays: z.number(),
    degradationPct: z.number(),
  }),
});

const costPerOunceImprovementInsight_ = z.object({
  type: z.literal(OmsInsightType.COST_PER_OUNCE_IMPROVEMENT),
  data: z.object({
    currentCostPerOz: z.number(),
    previousCostPerOz: z.number(),
    improvementPct: z.number(),
  }),
});

const costPerOunceDegradationInsight_ = z.object({
  type: z.literal(OmsInsightType.COST_PER_OUNCE_DEGRADATION),
  data: z.object({
    currentCostPerOz: z.number(),
    previousCostPerOz: z.number(),
    degradationPct: z.number(),
  }),
});

const totalShippingSpendMilestoneInsight_ = z.object({
  type: z.literal(OmsInsightType.TOTAL_SHIPPING_SPEND_MILESTONE),
  data: z.object({
    totalSpend: z.number(),
    changePct: z.number(),
    reason: z.string().optional(),
  }),
});

const channelConcentrationInsight_ = z.object({
  type: z.literal(OmsInsightType.CHANNEL_CONCENTRATION),
  data: z.object({
    channels: z.array(
      z.object({ name: z.string(), orderPct: z.number(), aov: z.number() }),
    ),
  }),
});

const fulfillmentVelocityTrendInsight_ = z.object({
  type: z.literal(OmsInsightType.FULFILLMENT_VELOCITY_TREND),
  data: z.object({
    dailyVolumeChangePct: z.number(),
    avgOrdersPerDay: z.number(),
  }),
});

const addressCorrectionImpactInsight_ = z.object({
  type: z.literal(OmsInsightType.ADDRESS_CORRECTION_IMPACT),
  data: z.object({
    correctionsCount: z.number(),
    estimatedSavings: z.number(),
  }),
});

const packingErrorReductionInsight_ = z.object({
  type: z.literal(OmsInsightType.PACKING_ERROR_REDUCTION),
  data: z.object({
    currentErrorRate: z.number(),
    previousErrorRate: z.number(),
    improvementPct: z.number(),
  }),
});

const packingErrorIncreaseInsight_ = z.object({
  type: z.literal(OmsInsightType.PACKING_ERROR_INCREASE),
  data: z.object({
    currentErrorRate: z.number(),
    previousErrorRate: z.number(),
    degradationPct: z.number(),
  }),
});

const throughputMilestoneInsight_ = z.object({
  type: z.literal(OmsInsightType.THROUGHPUT_MILESTONE),
  data: z.object({
    ordersPerHour: z.number(),
    rollingAvgOrdersPerHour: z.number(),
    milestonePeriodDays: z.number(),
  }),
});

const pickToPackImprovementInsight_ = z.object({
  type: z.literal(OmsInsightType.PICK_TO_PACK_IMPROVEMENT),
  data: z.object({
    currentAvgMinutes: z.number(),
    previousAvgMinutes: z.number(),
    improvementPct: z.number(),
  }),
});

const pickToPackDegradationInsight_ = z.object({
  type: z.literal(OmsInsightType.PICK_TO_PACK_DEGRADATION),
  data: z.object({
    currentAvgMinutes: z.number(),
    previousAvgMinutes: z.number(),
    degradationPct: z.number(),
  }),
});

const reclaimRevenueRecoveredInsight_ = z.object({
  type: z.literal(OmsInsightType.RECLAIM_REVENUE_RECOVERED),
  data: z.object({
    totalRecovered: z.number(),
    proactiveClaimsSubmitted: z.number(),
  }),
});

// ---------------------------------------------------------------------------
// Always-show summary insights
// ---------------------------------------------------------------------------

const periodShipmentSummaryInsight_ = z.object({
  type: z.literal(OmsInsightType.PERIOD_SHIPMENT_SUMMARY),
  data: z.object({
    totalShipments: z.number(),
    avgPerDay: z.number(),
    periodDays: z.number(),
  }),
});

const carrierCostSpreadInsight_ = z.object({
  type: z.literal(OmsInsightType.CARRIER_COST_SPREAD),
  data: z.object({
    cheapestCarrier: z.string(),
    cheapestAvgCost: z.number(),
    mostExpensiveCarrier: z.string(),
    mostExpensiveAvgCost: z.number(),
  }),
});

const fastestCarrierInsight_ = z.object({
  type: z.literal(OmsInsightType.FASTEST_CARRIER),
  data: z.object({
    carrier: z.string(),
    avgDaysToDeliver: z.number(),
    overallAvgDays: z.number(),
  }),
});

const busiestShippingDayInsight_ = z.object({
  type: z.literal(OmsInsightType.BUSIEST_SHIPPING_DAY),
  data: z.object({
    dayOfWeek: z.string(),
    avgShipmentsOnDay: z.number(),
    totalShipmentsOnDay: z.number(),
  }),
});

const topCarrierVolumeInsight_ = z.object({
  type: z.literal(OmsInsightType.TOP_CARRIER_VOLUME),
  data: z.object({
    carrier: z.string(),
    shipmentCount: z.number(),
    volumePct: z.number(),
    carrierCount: z.number(),
  }),
});

const avgCostPerLabelInsight_ = z.object({
  type: z.literal(OmsInsightType.AVG_COST_PER_LABEL),
  data: z.object({
    avgCost: z.number(),
    totalSpend: z.number(),
    totalLabels: z.number(),
  }),
});

// ---------------------------------------------------------------------------
// Discriminated union + shared metadata
// ---------------------------------------------------------------------------

const omsInsightBody_ = z.discriminatedUnion("type", [
  onTimeFulfillmentStreakInsight_,
  orderToShipTimeImprovementInsight_,
  orderToShipTimeDegradationInsight_,
  costPerOunceImprovementInsight_,
  costPerOunceDegradationInsight_,
  totalShippingSpendMilestoneInsight_,
  channelConcentrationInsight_,
  fulfillmentVelocityTrendInsight_,
  addressCorrectionImpactInsight_,
  packingErrorReductionInsight_,
  packingErrorIncreaseInsight_,
  throughputMilestoneInsight_,
  pickToPackImprovementInsight_,
  pickToPackDegradationInsight_,
  reclaimRevenueRecoveredInsight_,
  periodShipmentSummaryInsight_,
  carrierCostSpreadInsight_,
  fastestCarrierInsight_,
  busiestShippingDayInsight_,
  topCarrierVolumeInsight_,
  avgCostPerLabelInsight_,
]);

const omsInsight_ = z
  .object({
    generatedAt: z.date(),
    dismissedAt: z.date().optional(),
    periodStart: z.date(),
    periodEnd: z.date(),
    priority: z.number(),
  })
  .and(omsInsightBody_);

export type OmsInsight = z.infer<typeof omsInsight_>;
export const OmsInsightSchema: z.ZodType<OmsInsight> = omsInsight_;
